import * as DocumentPicker from "expo-document-picker";
import * as ImagePicker from "expo-image-picker";

type MediaTypeOption = "all" | "images" | "videos";

export const pickMedia = async (
  fromCamera = false,
  mediaType: MediaTypeOption = "all",
  maxFileSizeMB: number = 10 // Default 10MB
) => {
  try {
    let allowedMediaTypes: ImagePicker.MediaType | ImagePicker.MediaType[];
    switch (mediaType) {
      case "images":
        allowedMediaTypes = "images";
        break;
      case "videos":
        allowedMediaTypes = "videos";
        break;
      default:
        allowedMediaTypes = ["images", "videos"];
    }

    const permission = fromCamera
      ? await ImagePicker.requestCameraPermissionsAsync()
      : await ImagePicker.requestMediaLibraryPermissionsAsync();

    if (permission.status !== "granted") {
      alert("Permission is required to select media.");
      return null;
    }

    const result = fromCamera
      ? await ImagePicker.launchCameraAsync({
          mediaTypes: allowedMediaTypes,
          quality: 0.7,
        })
      : await ImagePicker.launchImageLibraryAsync({
          mediaTypes: allowedMediaTypes,
          allowsMultipleSelection: true,
          quality: 0.7,
        });

    if (result.canceled) return null;

    const maxSizeBytes = maxFileSizeMB * 1024 * 1024;
    const validAssets = result.assets.filter((asset) => {
      if (!asset.fileSize) return true; // If size unknown, allow it
      if (asset.fileSize > maxSizeBytes) {
        alert(`File "${asset.fileName || 'selected'}" exceeds maximum size of ${maxFileSizeMB}MB.`);
        return false;
      }
      return true;
    });

    if (validAssets.length === 0) return null;
    return validAssets;
  } catch (error) {
    console.error("pickMedia error:", error);
    return null;
  }
};

export const pickFile = async (
  multiple = false,
  maxFileSizeMB: number = 10 // Default 10MB
) => {
  try {
    const result = await DocumentPicker.getDocumentAsync({
      type: [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-powerpoint",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        "text/plain",
        "text/csv",
        "application/zip",
        "application/x-rar-compressed",
      ],
      copyToCacheDirectory: true,
      multiple, // Now properly using the multiple param
    });

    if (result.canceled) return null;

    const maxSizeBytes = maxFileSizeMB * 1024 * 1024;

    // DocumentPicker can return single object or array when multiple=true
    const assets = Array.isArray(result.assets) ? result.assets : [result];

    const validAssets = assets.filter((file) => {
      if (!file.size) return true; // size is optional
      if (file.size > maxSizeBytes) {
        alert(`File "${file.name}" exceeds maximum size of ${maxFileSizeMB}MB.`);
        return false;
      }
      return true;
    });

    if (validAssets.length === 0) return null;

    // Return same format as original
    return multiple ? { ...result, assets: validAssets } : validAssets[0];
  } catch (error) {
    console.error("pickFile error:", error);
    return null;
  }
};
